<template>
    <div class="wecom-page" v-show="false">
        <div class="wecom-bg">
            <div class="blob blob-a"></div>
            <div class="blob blob-b"></div>
            <div class="grid-mask"></div>
        </div>

        <div class="wecom-shell">
            <div class="brand-side">
                <div class="brand-mark">
                    <img class="brand-logo" :src="wecomIcon" alt="企业微信" />
                    <div>
                        <div class="brand-name">企业微信登录</div>
                        <div class="brand-sub">第三方应用 · 管理后台</div>
                    </div>
                </div>
                <h1>安全进入电子合同后台</h1>
                <p>使用已授权企业的企业微信成员身份登录。未绑定手机号也可先进入系统，使用相关功能时再完成绑定。</p>
                <ul class="tips">
                    <li>请确保当前成员已加入授权企业</li>
                    <li>绑定手机号须为已开通后台账号</li>
                    <li>管理员 / 副管理员 / 员工均可绑定登录</li>
                </ul>
            </div>

            <div class="panel-side">
                <div class="panel-card">
                    <div class="panel-title">企业微信第三方应用管理</div>
                    <div class="panel-desc">点击下方按钮，跳转企业微信授权完成登录</div>

                    <div v-if="statusText" class="status-box" :class="statusType">
                        <Icon :type="statusIcon" />
                        <span>{{ statusText }}</span>
                    </div>

                    <Button
                        class="login-btn"
                        type="primary"
                        long
                        size="large"
                        :loading="loading"
                        @click="startWecomLogin"
                    >
                        <span v-if="!loading" class="btn-inner">
                            <img class="btn-logo" :src="wecomIcon" alt="" />
                            企业微信授权登录
                        </span>
                        <span v-else>正在跳转授权...</span>
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { loginJump } from '@/api/interFace'
import { getWecomLoginUrl, wecomCallback } from '@/api/wecom'
import {
    getApiErrorMsg,
    markWecomLogin,
    isWecomLogin,
    applyUserSession,
    setCookies,
    getCookies,
    removeCookies,
    parseQuery,
    clearLocationSearch,
    saveWecomBindToken
} from '@/utils/helper'

const WECOM_CODE_USED_KEY = 'wecom_oauth_code_used'

/**
 * 企微统一入口页。
 * 授权回跳配置到本页后，企微会带临时 code 回来，例如：
 * - /#/admin/login/wecom?code=xxx
 * - 或 hash 路由下常见：/?code=xxx#/admin/login/wecom
 * 前端调用 wecom_callback 换取登录结果：
 * - 有 token / userinfo：直接进入后台（未绑定手机号也可先进系统）
 * - 仅 need_bind + bind_token 且无登录凭证：进入绑定页（兼容旧回跳）
 * - 业务接口返回 410005 时再引导绑定手机号
 * 绑定成功后也可带 token 回本页：/#/admin/login/wecom?token=xxx
 * 无回跳参数时：若 Cookie 仍有有效 token，自动进首页，免再次授权。
 */
export default {
    name: 'WecomLogin',
    data() {
        return {
            wecomIcon: require('@/assets/wecomimg/wecom.png'),
            loading: false,
            statusText: '',
            statusType: 'info'
        }
    },
    computed: {
        statusIcon() {
            if (this.statusType === 'error') return 'ios-alert'
            if (this.statusType === 'success') return 'ios-checkmark-circle'
            return 'ios-information-circle'
        }
    },
    created() {
        document.title = '企业微信登录 · 第三方应用管理'
        // 仅进入本页不标记；真正走企微授权/登录成功后再 mark，避免污染普通浏览器退出
        const handled = this.handleCallbackParams()
        // 无回跳/会话可处理时，隐藏 UI 并直接拉起企微授权
        if (!handled) {
            this.startWecomLogin()
        }
    },
    methods: {
        /** 兼容 hash 路由：code 可能在 # 前的 search，也可能在 route.query */
        pickQueryValue(key) {
            const routeQ = this.$route.query || {}
            if (routeQ[key] != null && routeQ[key] !== '') return routeQ[key]
            const hrefQ = parseQuery()
            return hrefQ[key] != null && hrefQ[key] !== '' ? hrefQ[key] : ''
        },
        clearAuthQuery() {
            // 清除一次性 code，避免刷新重复调用 wecom_callback
            if (this.$route.query && Object.keys(this.$route.query).length) {
                this.$router.replace({ path: '/admin/login/wecom', query: {} })
            }
            this.clearOAuthSearch()
        },
        /** 清掉 hash 前的 ?code=&state=（企微 OAuth 回跳特有） */
        clearOAuthSearch() {
            clearLocationSearch()
        },
        /**
         * 处理企微授权回跳 / 绑定成功回跳 / 本地 token 自动登录
         * @returns {boolean} 是否已接管后续流程（无需再 startWecomLogin）
         */
        handleCallbackParams() {
            const code = this.pickQueryValue('code')
            const token = this.pickQueryValue('token')
            const needBind = this.pickQueryValue('need_bind')
            const bindToken = this.pickQueryValue('bind_token')

            // 1) 企微 OAuth 回跳：用临时 code 调 wecom_callback
            if (code) {
                // 已用过的 code 残留在地址栏时（如退出后回到本页），直接清掉，禁止二次调用
                const usedCode = sessionStorage.getItem(WECOM_CODE_USED_KEY)
                if (usedCode && usedCode === code) {
                    this.clearOAuthSearch()
                    return false
                }
                this.handleWecomCode(code)
                return true
            }
            // 2) 未绑定参数直达绑定页（兼容后端直接拼 query 的情况）
            if (needBind == 1 || needBind === '1' || bindToken) {
                this.clearOAuthSearch()
                this.goBindPage({
                    bind_token: bindToken || '',
                    corpid: this.pickQueryValue('corpid') || '',
                    userid: this.pickQueryValue('userid') || ''
                })
                return true
            }
            // 3) 绑定成功：带 token 回本页后直接登录进后台
            if (token) {
                this.clearOAuthSearch()
                this.enterByToken(token)
                return true
            }
            // 4) 无回跳参数：仅本会话已是企微登录时才免授权进入，避免普通 Cookie 误进本页被标成企微
            const cachedToken = getCookies('token')
            if (cachedToken && isWecomLogin()) {
                this.tryAutoEnter()
                return true
            }
            return false
        },
        /** 用 Cookie 中的 token 校验会话，有效则进首页；失效则静默清掉，展示授权按钮 */
        tryAutoEnter() {
            if (!isWecomLogin()) {
                this.loading = false
                this.statusText = ''
                return
            }
            this.loading = true
            this.statusText = '检测到已登录，正在进入系统...'
            this.statusType = 'info'
            loginJump().then(res => {
                const data = (res && res.data) || {}
                markWecomLogin()
                if (applyUserSession(this.$store, data)) {
                    this.statusText = '登录有效，正在进入系统...'
                    this.statusType = 'success'
                    this.clearOAuthSearch()
                    this.$router.replace({ path: '/index' })
                } else {
                    this.clearLocalSession()
                    this.loading = false
                    this.statusText = ''
                }
            }).catch(() => {
                // token 失效时 request 拦截器可能已清 Cookie；此处只恢复登录页，不弹错误打扰用户
                this.clearLocalSession()
                this.loading = false
                this.statusText = ''
            })
        },
        clearLocalSession() {
            removeCookies('token')
            removeCookies('expires_time')
            removeCookies('uuid')
        },
        handleWecomCode(code) {
            this.loading = true
            this.statusText = '授权成功，正在校验登录...'
            this.statusType = 'info'
            markWecomLogin()
            // 先标记并清掉地址栏 code，避免进后台后再退出时二次消费
            sessionStorage.setItem(WECOM_CODE_USED_KEY, code)
            this.clearOAuthSearch()
            wecomCallback({ code }).then(res => {
                const data = (res && res.data) || {}
                if (data.bind_token) {
                    saveWecomBindToken(data.bind_token)
                }
                // 新逻辑：有登录凭证则直接进后台（未绑手机号也可进入，业务接口 410005 再引导绑定）
                const loginToken = data.token || this.extractTokenFromUrl(data.url)
                if (loginToken) {
                    this.enterByToken(loginToken)
                    return
                }
                if (data.userinfo) {
                    this.enterByLoginData(data)
                    return
                }
                // 兼容：仅返回 need_bind / bind_token、无登录凭证时仍进绑定页
                if (data.need_bind == 1 || data.need_bind === '1' || data.bind_token) {
                    this.goBindPage({
                        bind_token: data.bind_token || '',
                        corpid: data.corpid || '',
                        userid: data.userid || ''
                    })
                    return
                }
                this.loading = false
                this.clearAuthQuery()
                this.statusText = (res && res.msg) || '登录失败，请重试'
                this.statusType = 'error'
                this.$Message.error(this.statusText)
            }).catch(err => {
                this.loading = false
                this.clearAuthQuery()
                const msg = getApiErrorMsg(err, '授权登录失败')
                // 40029 invalid code：多为旧 code 残留，提示重新授权即可
                this.statusText = msg
                this.statusType = 'error'
                this.$Message.error(msg)
            })
        },
        goBindPage({ bind_token, corpid, userid }) {
            const query = { bind_token: bind_token || '' }
            if (corpid) query.corpid = corpid
            if (userid) query.userid = userid
            this.$router.replace({
                path: '/admin/login/wecomBind',
                query
            })
        },
        enterByLoginData(data) {
            this.loading = true
            this.statusText = '授权成功，正在进入系统...'
            this.statusType = 'success'
            markWecomLogin()
            // 有 token 时优先 loginJump，保证菜单与普通登录一致
            if (data.token) {
                this.enterByToken(data.token)
                return
            }
            if (applyUserSession(this.$store, data)) {
                this.clearOAuthSearch()
                this.$router.replace({ path: '/index' })
            } else {
                this.loading = false
                this.clearAuthQuery()
                this.statusText = '您已被限制登录'
                this.statusType = 'error'
                this.$Message.error('您已被限制登录')
            }
        },
        enterByToken(token) {
            this.loading = true
            this.statusText = '授权成功，正在进入系统...'
            this.statusType = 'success'
            markWecomLogin()
            setCookies('token', token)
            loginJump().then(res => {
                const data = (res && res.data) || {}
                markWecomLogin()
                if (applyUserSession(this.$store, data)) {
                    this.clearOAuthSearch()
                    this.$router.replace({ path: '/index' })
                } else {
                    removeCookies('token')
                    removeCookies('expires_time')
                    this.loading = false
                    this.clearAuthQuery()
                    this.statusText = '您已被限制登录'
                    this.statusType = 'error'
                    this.$Message.error('您已被限制登录')
                }
            }).catch(err => {
                this.loading = false
                this.clearAuthQuery()
                const msg = getApiErrorMsg(err, '登录失败')
                this.statusText = msg
                this.statusType = 'error'
                this.$Message.error(msg)
            })
        },
        extractTokenFromUrl(url) {
            if (!url || typeof url !== 'string') return ''
            const match = url.match(/[?&#]token=([^&#]+)/)
            return match ? decodeURIComponent(match[1]) : ''
        },
        startWecomLogin() {
            if (this.loading) return
            this.loading = true
            this.statusText = '正在获取企业微信授权链接...'
            this.statusType = 'info'
            markWecomLogin()
            getWecomLoginUrl().then(res => {
                const url = res && res.data && res.data.url
                if (!url) {
                    this.loading = false
                    this.statusText = '未获取到授权链接，请稍后重试'
                    this.statusType = 'error'
                    this.$Message.error('未获取到授权链接')
                    return
                }
                this.statusText = '即将跳转企业微信授权页...'
                this.statusType = 'success'
                window.location.href = url
            }).catch(err => {
                this.loading = false
                const msg = getApiErrorMsg(err, '获取授权链接失败')
                this.statusText = msg
                this.statusType = 'error'
                this.$Message.error(msg)
            })
        }
    }
}
</script>

<style scoped>
.wecom-page {
    --wq-green: #07c160;
    --wq-green-dark: #06ad56;
    --wq-ink: #122033;
    --wq-muted: #66758a;
    --wq-card: rgba(255, 255, 255, 0.92);
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    font-family: "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
    color: var(--wq-ink);
    background: #eef5f0;
}

.wecom-bg {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(135deg, #d8f3e4 0%, #eef6fb 45%, #f4f7f5 100%);
}

.blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    opacity: 0.55;
}

.blob-a {
    width: 420px;
    height: 420px;
    left: -80px;
    top: -60px;
    background: #8ee0b3;
}

.blob-b {
    width: 460px;
    height: 460px;
    right: -120px;
    bottom: -100px;
    background: #a8d4f5;
}

.grid-mask {
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(rgba(7, 193, 96, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(7, 193, 96, 0.05) 1px, transparent 1px);
    background-size: 36px 36px;
    mask-image: radial-gradient(circle at center, #000 30%, transparent 85%);
}

.wecom-shell {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    max-width: 1080px;
    margin: 0 auto;
    padding: 48px 24px;
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    gap: 36px;
    align-items: center;
}

.brand-side h1 {
    margin: 28px 0 14px;
    font-size: 34px;
    line-height: 1.25;
    letter-spacing: 0.5px;
}

.brand-side > p {
    margin: 0;
    color: var(--wq-muted);
    font-size: 15px;
    line-height: 1.7;
    max-width: 460px;
}

.brand-mark {
    display: flex;
    align-items: center;
    gap: 14px;
}

.brand-logo {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    object-fit: contain;
    display: block;
}

.btn-logo {
    width: 18px;
    height: 18px;
    object-fit: contain;
    display: block;
}

.brand-name {
    font-size: 18px;
    font-weight: 700;
}

.brand-sub {
    margin-top: 2px;
    font-size: 13px;
    color: var(--wq-muted);
}

.tips {
    margin: 28px 0 0;
    padding: 0;
    list-style: none;
}

.tips li {
    position: relative;
    padding: 8px 0 8px 18px;
    color: #415168;
    font-size: 14px;
}

.tips li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 15px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--wq-green);
}

.panel-card {
    background: var(--wq-card);
    border: 1px solid rgba(18, 32, 51, 0.06);
    border-radius: 20px;
    padding: 36px 32px;
    box-shadow: 0 18px 50px rgba(18, 56, 36, 0.08);
    backdrop-filter: blur(10px);
}

.panel-title {
    font-size: 22px;
    font-weight: 700;
}

.panel-desc {
    margin-top: 8px;
    color: var(--wq-muted);
    font-size: 14px;
    line-height: 1.6;
}

.status-box {
    margin-top: 22px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
}

.status-box.info {
    background: #f0f7ff;
    color: #2f6fed;
}

.status-box.success {
    background: #edfaf2;
    color: #079a4d;
}

.status-box.error {
    background: #fff1f0;
    color: #d4380d;
}

.login-btn {
    margin-top: 26px;
    height: 48px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--wq-green), var(--wq-green-dark));
    font-size: 16px;
    font-weight: 600;
}

.login-btn:hover {
    background: linear-gradient(135deg, #12cc6b, #059a4c);
}

.btn-inner {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

@media (max-width: 900px) {
    .wecom-shell {
        grid-template-columns: 1fr;
        padding: 28px 16px 40px;
    }

    .brand-side h1 {
        font-size: 28px;
        margin-top: 20px;
    }
}
</style>
